import {
  afterNextRender,
  AfterViewChecked,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CurrencyBalances, WalletBalances } from '@olv/ui-shared/models/wallet.model';
import { AssetBalance, groupBalancesByAsset } from '@olv/ui-shared/utils/asset-balance.utils';
import { SplitPartsPipe } from '@olv/ui-shared/pipes/split-parts.pipe';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { CurrencyIconComponent } from '../currency-icon/currency-icon.component';
import { RiskDistributionTooltipComponent } from '../risk-distribution-tooltip/risk-distribution-tooltip.component';

@Component({
  host: { 'animate.enter': 'slide-in-right', 'animate.leave': 'slide-out-right' },
  selector: 'app-asset-balance-drawer',
  standalone: true,
  imports: [MatIconModule, SplitPartsPipe, CurrencyIconComponent, RiskDistributionTooltipComponent],
  templateUrl: './asset-balance-drawer.component.html',
  styleUrls: ['./asset-balance-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AssetBalanceDrawerComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() walletBalances: WalletBalances | null = null;
  @Input() isLoading = false;
  @Input() error: string | null = null;
  @Output() closed = new EventEmitter<void>();
  @HostBinding('class.details-aside') readonly asideClass = true;
  @ViewChild('riskPopup') riskPopup?: ElementRef<HTMLElement>;
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly merchantService = inject(MerchantService);
  private readonly dialog = inject(MatDialog);
  protected readonly metadata = inject(NetworkMetadataService);

  private readonly balances = signal<CurrencyBalances[]>([]);
  readonly assets = computed(() => groupBalancesByAsset(this.balances(), this.metadata));
  hoveredEntry: CurrencyBalances | null = null;
  popupTop = 0;
  popupLeft = 0;
  readonly skeletonRows = [1, 2, 3];
  private readonly collapsed = new Set<string>();
  private popupNeedsClamp = false;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly popupWidth = 340;
  private readonly popupGap = 12;
  private readonly viewportPadding = 16;
  private opened = false;
  private readonly previousFocus = document.activeElement as HTMLElement | null;

  constructor() {
    this.metadata.load();
    afterNextRender(() => this.closeButton?.nativeElement.focus({ preventScroll: true }));

    effect(() => {
      if (this.merchantService.sidebarOpenSignal() && this.opened) {
        this.closed.emit();
      }
    });
  }

  ngOnInit(): void {
    this.merchantService.closeSidebar();
    this.merchantService.assetsDrawerOpenSignal.set(true);
    setTimeout(() => (this.opened = true));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['walletBalances']) {
      this.balances.set(this.walletBalances?.currencyBalances ?? []);
    }
  }

  ngOnDestroy(): void {
    this.merchantService.assetsDrawerOpenSignal.set(false);
    this.clearHideTimeout();
    if (this.host.nativeElement.contains(document.activeElement)) {
      this.previousFocus?.focus({ preventScroll: true });
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.dialog.openDialogs.length) return;
    this.closed.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!this.opened || !target) return;
    if (this.host.nativeElement.contains(target) || target.closest('.show-assets-btn, [appTabStrip]')) return;
    this.closed.emit();
  }

  isExpanded(asset: AssetBalance): boolean {
    return !this.collapsed.has(asset.asset);
  }

  toggleAsset(asset: AssetBalance): void {
    if (asset.entries.length < 2) return;
    if (this.collapsed.has(asset.asset)) {
      this.collapsed.delete(asset.asset);
    } else {
      this.collapsed.add(asset.asset);
    }
  }

  showRisk(entry: CurrencyBalances, event: Event): void {
    if (!entry.riskDistribution?.length) return;
    this.clearHideTimeout();

    const row = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const panel = this.host.nativeElement.getBoundingClientRect();
    const width = Math.min(this.popupWidth, window.innerWidth - this.viewportPadding * 2);
    const fitsLeft = panel.left - this.popupGap - width >= this.viewportPadding;

    this.popupLeft = fitsLeft ? panel.left - this.popupGap - width : Math.max(this.viewportPadding, panel.left);
    this.popupTop = fitsLeft ? row.top : row.bottom + 6;
    this.hoveredEntry = entry;
    this.popupNeedsClamp = true;
  }

  ngAfterViewChecked(): void {
    if (!this.popupNeedsClamp || !this.riskPopup) return;
    this.popupNeedsClamp = false;

    const popup = this.riskPopup.nativeElement;
    const maxTop = window.innerHeight - this.viewportPadding - popup.offsetHeight;
    const top = this.popupTop > maxTop ? Math.max(this.viewportPadding, maxTop) : this.popupTop;
    popup.style.top = `${top}px`;
  }

  keepRiskOpen(): void {
    this.clearHideTimeout();
  }

  hideRisk(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => {
      this.hoveredEntry = null;
      this.hideTimeout = null;
    }, 120);
  }

  private clearHideTimeout(): void {
    if (!this.hideTimeout) return;
    clearTimeout(this.hideTimeout);
    this.hideTimeout = null;
  }
}
